import { config } from "config/config";
import {
  analyticsEnvironment,
  analyticsLocale,
  readAppVersion,
  shouldEnableYeloAppAnalytics,
  yeloAppAnalyticsUserId,
} from "utils/analytics.util";

/**
 * The only module that talks to Mixpanel.
 * Every event is marked app=yelo_app so a shared project can separate this
 * webview from the other apps.
 */

type MixpanelClient = {
  init: (token: string, config?: Record<string, unknown>) => void;
  track: (event: string, properties?: Record<string, unknown>) => void;
  identify: (id: string) => void;
  reset: () => void;
  register: (props: Record<string, unknown>) => void;
  people: { set: (props: Record<string, unknown>) => void };
};

const APP = "yelo_app";
const FLOW_KEY = "yelo_analytics_flow_id";
const FLOW_DONE_KEY = "yelo_analytics_flow_done";
const SOURCE_KEY = "yelo_analytics_source";
const NEXT_SOURCE_KEY = "yelo_analytics_next_source";
const SEARCH_KEY = "yelo_analytics_search";
const JOURNEY_KEY = "yelo_analytics_journey";
const ATTEMPT_KEY = "yelo_analytics_attempt";
const PAYMENT_KEY = "yelo_analytics_payment";

type JourneySnapshot = {
  service_type?: "transfer" | "hourly";
  vehicle_class?: string;
  price?: number;
  currency?: string;
  booking_id?: string;
  request_id?: string;
};

let loading: Promise<MixpanelClient | null> | null = null;
let paymentStartLock = false;

function superProperties(): Record<string, unknown> {
  const version = readAppVersion();
  return {
    app: APP,
    platform: "yelo_app_webview",
    locale: analyticsLocale(),
    environment: analyticsEnvironment(),
    ...(version ? { app_version: version } : {}),
  };
}

function getMixpanel(): Promise<MixpanelClient | null> {
  if (typeof window === "undefined" || !shouldEnableYeloAppAnalytics()) {
    return Promise.resolve(null);
  }

  const token = config.MIXPANEL_TOKEN;
  if (!token) return Promise.resolve(null);

  if (!loading) {
    loading = import("mixpanel-browser")
      .then(({ default: mixpanel }) => {
        try {
          const env = analyticsEnvironment();
          mixpanel.init(token, {
            persistence: "localStorage",
            track_pageview: false,
            debug: env !== "production",
            api_host: "https://api-eu.mixpanel.com",
          });
          mixpanel.register(superProperties());
          return mixpanel;
        } catch {
          return null;
        }
      })
      .catch(() => {
        loading = null;
        return null;
      });
  }

  return loading;
}

function track(event: string, properties?: Record<string, unknown>) {
  const payload = {
    ...superProperties(),
    ...(properties || {}),
  };
  void getMixpanel()
    .then((client) => {
      try {
        client?.track(event, payload);
      } catch {
        /* analytics must never break the booking flow */
      }
    })
    .catch(() => {});
}

function seen(key: string): boolean {
  try {
    if (sessionStorage.getItem(key)) return true;
    sessionStorage.setItem(key, "1");
    return false;
  } catch {
    return false;
  }
}

function readJson<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore quota / private mode */
  }
}

function newFlowId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `flow_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

export function currentFlowId(): string {
  try {
    return sessionStorage.getItem(FLOW_KEY) || "";
  } catch {
    return "";
  }
}

/** First show of the booking form, or the visit after a finished journey. */
export function ensureBookingFlow(): { flowId: string; source: "direct" | "book_again" } {
  let source: "direct" | "book_again" = "direct";
  try {
    const done = sessionStorage.getItem(FLOW_DONE_KEY) === "1";
    const queued = sessionStorage.getItem(NEXT_SOURCE_KEY);
    if (done || queued === "book_again" || !sessionStorage.getItem(FLOW_KEY)) {
      if (queued === "book_again") source = "book_again";
      const id = newFlowId();
      sessionStorage.setItem(FLOW_KEY, id);
      sessionStorage.setItem(SOURCE_KEY, source);
      sessionStorage.removeItem(FLOW_DONE_KEY);
      sessionStorage.removeItem(NEXT_SOURCE_KEY);
      sessionStorage.setItem(SEARCH_KEY, "0");
    } else {
      source = sessionStorage.getItem(SOURCE_KEY) === "book_again" ? "book_again" : "direct";
    }
  } catch {
    return { flowId: "", source: "direct" };
  }
  return { flowId: currentFlowId(), source };
}

/** The next time the booking form is shown, it starts a new journey. */
export function markNextBookingAsBookAgain() {
  try {
    sessionStorage.setItem(NEXT_SOURCE_KEY, "book_again");
    sessionStorage.setItem(FLOW_DONE_KEY, "1");
  } catch {
    /* ignore */
  }
}

function finishFlow() {
  try {
    sessionStorage.setItem(FLOW_DONE_KEY, "1");
  } catch {
    /* ignore */
  }
}

function searchNonce(): string {
  try {
    return sessionStorage.getItem(SEARCH_KEY) || "0";
  } catch {
    return "0";
  }
}

function bumpSearch(): string {
  const next = String(Number(searchNonce()) + 1);
  try {
    sessionStorage.setItem(SEARCH_KEY, next);
  } catch {
    /* ignore */
  }
  return next;
}

function rememberJourney(patch: JourneySnapshot) {
  const current = readJson<JourneySnapshot>(JOURNEY_KEY) || {};
  writeJson(JOURNEY_KEY, { ...current, ...patch });
}

function journey(): JourneySnapshot {
  return readJson<JourneySnapshot>(JOURNEY_KEY) || {};
}

function flowProps(): Record<string, unknown> {
  const flowId = currentFlowId();
  return flowId ? { flow_id: flowId } : {};
}

export function identifyYeloAppUser() {
  const userId = yeloAppAnalyticsUserId();
  if (!userId) return;
  void getMixpanel()
    .then((client) => {
      try {
        if (!client) return;
        client.identify(userId);
        client.people.set({ app: APP });
      } catch {
        /* ignore */
      }
    })
    .catch(() => {});
}

export function resetYeloAppAnalytics() {
  void getMixpanel()
    .then((client) => {
      try {
        client?.reset();
        client?.register(superProperties());
      } catch {
        /* ignore */
      }
    })
    .catch(() => {});
}

export function trackBookingFormViewed(serviceType: "transfer" | "hourly") {
  const { flowId, source } = ensureBookingFlow();
  if (!flowId || seen(`booking_form_viewed:${flowId}`)) return;
  rememberJourney({ service_type: serviceType });
  track("booking_form_viewed", {
    flow_id: flowId,
    service_type: serviceType,
    source,
  });
}

export function trackSearchSubmitted(properties: {
  service_type: "transfer" | "hourly";
  pickup_city?: string;
  dropoff_city?: string;
  pickup_kind: "airport" | "address";
  hours_booked?: number;
  lead_time_hours?: number;
}) {
  const flowId = ensureBookingFlow().flowId;
  bumpSearch();
  rememberJourney({ service_type: properties.service_type });
  const payload: Record<string, unknown> = {
    flow_id: flowId,
    service_type: properties.service_type,
    pickup_kind: properties.pickup_kind,
  };
  if (properties.pickup_city) payload.pickup_city = properties.pickup_city;
  if (properties.dropoff_city) payload.dropoff_city = properties.dropoff_city;
  if (properties.service_type === "hourly" && properties.hours_booked != null) {
    payload.hours_booked = properties.hours_booked;
  }
  if (properties.lead_time_hours != null) payload.lead_time_hours = properties.lead_time_hours;
  track("search_submitted", payload);
}

export function trackVehicleListViewed(properties: {
  vehicle_classes_count: number;
  lowest_price?: number;
  currency?: string;
}) {
  const flowId = currentFlowId();
  const nonce = searchNonce();
  if (!flowId || seen(`vehicle_list_viewed:${flowId}:${nonce}`)) return;
  if (properties.currency) rememberJourney({ currency: properties.currency });
  track("vehicle_list_viewed", {
    flow_id: flowId,
    vehicle_classes_count: properties.vehicle_classes_count,
    ...(properties.lowest_price != null ? { lowest_price: properties.lowest_price } : {}),
    ...(properties.currency ? { currency: properties.currency } : {}),
  });
}

export function trackVehicleSelected(properties: {
  vehicle_class: string;
  price?: number;
  currency?: string;
  list_position: number;
}) {
  const flowId = currentFlowId();
  if (!flowId || !properties.vehicle_class) return;
  if (seen(`vehicle_selected:${flowId}:${searchNonce()}:${properties.vehicle_class}`)) return;
  rememberJourney({
    vehicle_class: properties.vehicle_class,
    price: properties.price,
    currency: properties.currency,
  });
  track("vehicle_selected", {
    flow_id: flowId,
    vehicle_class: properties.vehicle_class,
    list_position: properties.list_position,
    ...(properties.price != null ? { price: properties.price } : {}),
    ...(properties.currency ? { currency: properties.currency } : {}),
  });
}

export function trackCheckoutViewed() {
  const flowId = currentFlowId();
  if (!flowId || seen(`checkout_viewed:${flowId}:${searchNonce()}`)) return;
  const saved = journey();
  track("checkout_viewed", {
    flow_id: flowId,
    ...(saved.vehicle_class ? { vehicle_class: saved.vehicle_class } : {}),
    ...(saved.price != null ? { price: saved.price } : {}),
    ...(saved.currency ? { currency: saved.currency } : {}),
  });
}

export function rememberPaymentContext(properties: {
  bookingId: string;
  requestId?: string;
  amount?: number;
  currency?: string;
}) {
  rememberJourney({
    booking_id: properties.bookingId,
    request_id: properties.requestId || properties.bookingId,
    price: properties.amount,
    currency: properties.currency,
  });
  writeJson(PAYMENT_KEY, {
    booking_id: properties.bookingId,
    request_id: properties.requestId || properties.bookingId,
    amount: properties.amount,
    currency: properties.currency,
  });
}

function paymentContext(): {
  booking_id?: string;
  request_id?: string;
  amount?: number;
  currency?: string;
} {
  return (
    readJson(PAYMENT_KEY) || {
      booking_id: journey().booking_id,
      request_id: journey().request_id,
      amount: journey().price,
      currency: journey().currency,
    }
  );
}

function attemptMap(): Record<string, number> {
  return readJson<Record<string, number>>(ATTEMPT_KEY) || {};
}

function nextAttempt(requestId: string): number {
  const map = attemptMap();
  const next = Number(map[requestId] || 0) + 1;
  map[requestId] = next;
  writeJson(ATTEMPT_KEY, map);
  return next;
}

function currentAttempt(requestId: string): number {
  return Number(attemptMap()[requestId] || 0);
}

export function trackPaymentStarted(cardBrand?: string) {
  if (paymentStartLock) return;
  const ctx = paymentContext();
  const requestId = ctx.request_id || ctx.booking_id || "";
  const flowId = currentFlowId();
  if (!flowId || !requestId) return;
  paymentStartLock = true;
  window.setTimeout(() => {
    paymentStartLock = false;
  }, 1500);
  const attempt = nextAttempt(requestId);
  if (seen(`payment_started:${flowId}:${requestId}:${attempt}`)) return;
  track("payment_started", {
    flow_id: flowId,
    request_id: requestId,
    attempt_number: attempt,
    ...(ctx.amount != null ? { amount: ctx.amount } : {}),
    ...(ctx.currency ? { currency: ctx.currency } : {}),
    ...(cardBrand ? { card_brand: cardBrand } : {}),
  });
}

export function trackPaymentFailed(properties: {
  reason_category: "declined" | "cancelled" | "error";
  unconfirmed?: boolean;
}) {
  const ctx = paymentContext();
  const requestId = ctx.request_id || ctx.booking_id || "";
  const flowId = currentFlowId();
  const attempt = currentAttempt(requestId) || 1;
  if (!flowId || !requestId) return;
  if (seen(`payment_failed:${flowId}:${requestId}:${attempt}`)) return;
  track("payment_failed", {
    flow_id: flowId,
    request_id: requestId,
    attempt_number: attempt,
    reason_category: properties.reason_category,
    unconfirmed: properties.unconfirmed !== false,
  });
}

export function trackPaymentRecoverySelected(action: "try_again" | "book_again") {
  const ctx = paymentContext();
  const requestId = ctx.request_id || ctx.booking_id || "";
  const flowId = currentFlowId();
  if (!flowId) return;
  track("payment_recovery_selected", {
    flow_id: flowId,
    ...(requestId ? { request_id: requestId } : {}),
    action,
  });
  if (action === "book_again") markNextBookingAsBookAgain();
}

export function trackBookingConfirmed(bookingId: string) {
  if (!bookingId || seen(`booking_confirmed:${bookingId}`)) return;
  const saved = journey();
  const requestId = saved.request_id || bookingId;
  track("booking_confirmed", {
    ...flowProps(),
    request_id: requestId,
    booking_id: bookingId,
    ...(saved.price != null ? { amount: saved.price } : {}),
    ...(saved.currency ? { currency: saved.currency } : {}),
    ...(saved.vehicle_class ? { vehicle_class: saved.vehicle_class } : {}),
    ...(saved.service_type ? { service_type: saved.service_type } : {}),
    attempts_count: currentAttempt(requestId) || 1,
  });
  finishFlow();
}

export function trackWebviewExited(reason: "back_to_yelo" | "payment_failed" | "session_expired") {
  track("webview_exited", {
    ...flowProps(),
    reason,
  });
}

export function readSelectedCardBrand(): string | undefined {
  try {
    const selected = document.querySelector(
      ".wpwl-brand.wpwl-selected, .wpwl-brand.wpwl-brand-selected, .wpwl-tab-card.wpwl-selected"
    );
    const hint = `${selected?.getAttribute("data-brand") || ""} ${selected?.className || ""}`.toLowerCase();
    const control = document.querySelector<HTMLSelectElement | HTMLInputElement>(
      ".wpwl-control-brand"
    );
    const value = `${hint} ${control?.value || ""}`.toLowerCase();
    if (value.includes("mada")) return "mada";
    if (value.includes("visa")) return "visa";
    if (value.includes("master")) return "mastercard";
    if (value.includes("apple")) return "applepay";
    return undefined;
  } catch {
    return undefined;
  }
}
